<template>
    <div
        class="grid text-[14px] font-oceanside"
        :class="containerJustifyClass"
    >
        <div
            class="w-full h-full max-w-[240px] lg:text-right"
        >
            <div
                v-if="isFrontGroup"
                class="grid gap-y-[4px] break-words leading-[18px]"
            >
                <template v-if="selections.length > 0">
                    <div
                        v-for="(name, index) in selections"
                        :key="index + 'selection'"
                    >
                        {{ name }}
                    </div>
                </template>
                <template v-else-if="selections.length === 0 && !hasCompletePaintfill">
                    <template v-if="context === 'main'">
                        {{ addPlaceholderCopy }}
                    </template>
                    <template v-else>
                        <double-dash />
                    </template>
                </template>
                <paintfill-summary-link v-if="hasCompletePaintfill" />
            </div>
            <div
                v-else
                class="break-words leading-[18px] flex flex-col justify-center h-full text-left lg:text-right"
                :class="textAlignClass"
            >
                <div v-if="hasCompletePaintfill" class="flex justify-end">
                    <paintfill-summary-link />
                </div>
                <div v-else-if="isSurefitHubLocked">
                    <lock-icon
                        v-if="isSurefitHubLocked"
                        class="relative inline-block text-gray-dark w-[32px] shrink-0 top-[-2px]"
                    />&nbsp;{{joinedSelectionsFirstWord}} {{joinedSelectionsRest}}
                </div>
                <div v-else>
                    {{ joinedSelections }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import DoubleDash from './DoubleDash.vue';
    import LockIcon from './LockIcon.vue';

    import PaintfillSummaryLink from './PaintfillSummaryLink.vue';

    export default {
        mixins: [allStores],

        components: {
            PaintfillSummaryLink,
            DoubleDash,
            // eslint-disable-next-line vue/no-unused-components
            LockIcon,
        },

        props: {
            placementData: {
                type: Object,
                required: true,
            },
            hasErrors: {
                type: Boolean,
                default: false,
            },
            justifyTextEnd: {
                type: Boolean,
                default: false,
            },
            centerText: {
                type: Boolean,
                default: false,
            },
            context: {
                type: String,
                default: 'main',
                validator: (value) => ['main', 'panel'].includes(value),
            },
        },

        data() {
            return {};
        },

        computed: {
            isFrontGroup() {
                return this.placementData.name === 'Face/Sole' || this.placementData.code === 'Face/Sole';
            },

            isMobile() {
                return this.appStore?.isMobile;
            },

            addPlaceholderCopy() {
                return this.mobileAddPlaceholderCopy || this.$t('ADD');
            },

            mobileAddPlaceholderCopy() {
                if (!this.isMobile || this.context !== 'main') {
                    return null;
                }

                if (this.isFrontGroup) {
                    return 'Personalize Initials, Stamping, and Paintfill';
                }

                if (this.placementData.name === 'Top' || this.placementData.code === 'TopPaintfill') {
                    return 'Personalize with Paintfill';
                }

                if (this.placementData.name === 'Back' || this.placementData.code === 'BackPaintfill') {
                    return 'Personalize with Paintfill';
                }

                return null;
            },

            hasFrontPaintfill() {
                if (!this.isFrontGroup) {
                    return false;
                }

                return this.customizerStore.paintfillColorPlacementCodesForGroup('Face/Sole')
                    .some((placementCode) => this.customizerStore.isColorPlacementSelected(placementCode));
            },

            containerJustifyClass() {
                if (this.centerText) {
                    return 'justify-items-center';
                }

                if (this.justifyTextEnd) {
                    return 'justify-items-end';
                }

                return 'justify-items-start';
            },

            joinedSelections() {
                return this.selections.join(' | ');
            },

            joinedSelectionsFirstWord() {
                const parts = this.joinedSelections.split(' ');

                return parts[0] || '';
            },

            joinedSelectionsRest() {
                const parts = this.joinedSelections.split(' ');

                return parts.length > 1 ? parts.slice(1).join(' ') : '';
            },

            textAlignClass() {
                if (this.centerText) {
                    return 'text-center';
                }

                if (this.justifyTextEnd) {
                    return 'text-right';
                }

                return 'text-left';
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            selectedProducts() {
                return this.customizerStore.selectedProducts;
            },

            placements() {
                const visible = [];

                if (!this.placementData) { return visible; }

                let placementsSource = this.placementData.placements;

                // For unified Front group in contexts where placements aren't provided,
                // compose them from Character Stamping and Stamps groups.
                if ((!placementsSource || placementsSource.length === 0)
                    && this.isFrontGroup) {
                    const characterStampingGroup = this.customizerStore.visibleGroups.find((g) => g.name === 'Character Stamping');
                    const stampsGroup = this.customizerStore.visibleGroups.find((g) => g.name === 'Stamps');

                    placementsSource = [
                        ...((characterStampingGroup && characterStampingGroup.placements) || []),
                        ...((stampsGroup && stampsGroup.placements) || []),
                    ];
                }

                if (!placementsSource || placementsSource.length === 0) { return visible; }

                placementsSource.forEach((placement) => {
                    if (placement.custom?.['v-show-selected'] === 'no') {
                        return;
                    }

                    visible.push(placement);
                });

                return visible;
            },

            hasCompletePaintfill() {
                return (this.placementData.code === 'TopPaintfill' && this.customizerStore.isPaintfillComplete('Top'))
                    || (this.placementData.code === 'BackPaintfill' && this.customizerStore.isPaintfillComplete('Back'))
                    || (this.placementData.code === 'Face/Sole' && this.customizerStore.isPaintfillComplete('Face/Sole'));
            },

            noCustomizationSelection() {
                if (
                    (this.placementData.name === 'Top' || this.placementData.code === 'TopPaintfill')
                    && this.customizerStore.isTopNoCustomizationSelected()
                ) {
                    return this.$t('NO_CUSTOMIZATION');
                }

                if (
                    (this.placementData.name === 'Back' || this.placementData.code === 'BackPaintfill')
                    && this.customizerStore.isBackNoCustomizationSelected()
                ) {
                    return this.$t('NO_CUSTOMIZATION');
                }

                return null;
            },

            paintfillRequiredSelection() {
                if (
                    (this.placementData.name === 'Top' || this.placementData.code === 'TopPaintfill')
                    && this.isAnotherPaintfillComplete('Top')
                    && !this.customizerStore.isTopPaintfillComplete()
                ) {
                    return this.$t('PAINTFILL_REQUIRED');
                }

                if (
                    (this.placementData.name === 'Back' || this.placementData.code === 'BackPaintfill')
                    && this.isAnotherPaintfillComplete('Back')
                    && !this.customizerStore.isBackPaintfillComplete()
                ) {
                    return this.$t('PAINTFILL_REQUIRED');
                }

                return null;
            },

            selections() {
                // Handle the Front group specially - it combines Character Stamping and Stamps
                if (this.isFrontGroup) {
                    return this.getSelectionsForFrontGroup();
                }

                if (this.paintfillRequiredSelection) {
                    return [this.paintfillRequiredSelection];
                }

                if (this.noCustomizationSelection) {
                    return [this.noCustomizationSelection];
                }

                const names = [];

                // Potential cases:
                //
                // First placement is important and toggles other placements.
                // Multiple "equal" placements.

                let hasMain = false;

                // If the first placement is "important" (toggles other placements in a group) and a placeholder, short circuit the evaluation.
                if (this.placements.length > 0) {
                    const firstPlacement = this.placements[0];

                    const firstSelection = this.viewController?.selectedAtPlacement(firstPlacement.code);

                    hasMain = true;

                    if (firstSelection.code === 'Stamp-Stamp-NoThanks') {
                        return ['No Toe Engraving'];
                    }

                    if (firstSelection.code === 'StampingStyle-StampingStyle-1') {
                        return ['No Character Stamping'];
                    }

                    if (firstPlacement.code === 'Shaft' && firstSelection?.placeholder) {
                        return [this.$t('ADD')];
                    }

                    if (firstPlacement.code === 'Grip' && firstSelection?.placeholder) {
                        return [this.$t('PERSONALIZE_GRIP')];
                    }

                    if (firstPlacement.code === 'Headcover' && firstSelection?.placeholder) {
                        return [this.$t('PERSONALIZE_HEADCOVER')];
                    }

                    if (firstPlacement.code === 'PutterShaftBand') {
                        if (firstSelection?.placeholder) {
                            return [this.$t('PERSONALIZE_SHAFT_BAND')];
                        }
                    }

                    // Should be the last check.
                    if (firstSelection?.placeholder) {
                        // TopPaintfill
                        if (firstPlacement.code === 'TopLineColor') {
                            // error state
                            if (this.customizerStore.isAnyPaintfillSelected() && !this.customizerStore.isTopPaintfillComplete()) {
                                return [this.$t('PAINTFILL_REQUIRED')];
                            }
                            return [this.addPlaceholderCopy];
                        }
                        // BackPaintfill
                        if (firstPlacement.code === 'MilledLogoColor') {
                            // error state
                            if (this.customizerStore.isAnyPaintfillSelected() && !this.customizerStore.isBackPaintfillComplete()) {
                                return [this.$t('PAINTFILL_REQUIRED')];
                            }
                            return [this.addPlaceholderCopy];
                        }
                        return [this.addPlaceholderCopy];
                    }
                }

                let index = 0;

                this.placements.forEach((placement) => {
                    // Special case for putters.
                    // This will skip PutterParent too.
                    if (!(placement.code === 'Putter')) {
                        return;
                    }

                    const orientations = new Set();
                    const lengths = new Set();

                    // eslint-disable-next-line no-restricted-syntax
                    for (const p of this.selectedProducts) {
                        const orientation = p.custom?.['t-orientation-name'];
                        if (orientation) {
                            orientations.add(orientation);
                        }

                        const length = p.custom?.['t-setcomp-name'];

                        if (length) {
                            lengths.add(length);
                        }
                    }

                    if (lengths.size > 0) {
                        names.push(`${this.$t('LENGTH')}: ${[...lengths].join(', ')}`);
                    }

                    if (orientations.size > 0) {
                        names.push(`${this.$t('HAND')}: ${[...orientations].join(', ')}`);
                    }
                });

                const noop = () => {};

                this.placements.forEach((placement) => {
                    // Special case for putters.
                    if (placement.code === 'Putter') {
                        return;
                    }

                    // Special case for putters.
                    if (placement.code === 'PutterParent') {
                        return;
                    }

                    // Skip initials colors - these are handled when displaying text with their colors.
                    if (placement.code === 'StampingColorsLine1' || placement.code === 'StampingColorsLine2' || placement.code === 'StampingColorsSymbols') {
                        return;
                    }

                    const selectedAtPlacement = this.viewController?.selectedAtPlacement(placement.code);
                    const selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;

                    // Special case for Flight Lines.
                    if (!selection && placement.code === 'FlightLine') {
                        if (selectedAtPlacement.placeholder && selectedAtPlacement.placeholderOptional) {
                            names.push(this.$t('NO_FLIGHT_LINES'));
                        }
                        return;
                    }

                    if (selection) {
                        // Special case for Shafts...
                        if (placement.code === 'Shaft') {
                            const manufacturer = selectedAtPlacement.custom?.['v-manufacturer'];
                            const name = selectedAtPlacement.custom?.['v-name'];

                            // Putters have no flex or length.
                            names.push(manufacturer);
                            names.push(`${name}`);
                        } else if (placement.code === 'Grip') {
                            // Special case for Grips...
                            const name = selectedAtPlacement.custom?.['v-name'];

                            names.push(`${name}`);
                        } else if (placement.code === 'StampingColorsLoft' || placement.code === 'LoftAdjustment') {
                            names.push(`${this.$t('CURRENT_LOFT')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'StampingColorsBounce') {
                            names.push(`${this.$t('CURRENT_BOUNCE')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'StampingColorsGrind') {
                            names.push(`${this.$t('CURRENT_GRIND')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'StampingColorsWing') {
                            names.push(`${this.$t('CURRENT_WING')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'StampColor' || placement.code === 'FlightLine') {
                            names.push(`${this.$t('CURRENT_COLOR')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'StampSymbols') {
                            noop(); // handled below in stamping section
                        } else if (placement.code === 'WeightAdjustment') {
                            names.push(`${this.$t('CURRENT_WEIGHT')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'LieAdjustment') {
                            names.push(`${this.$t('CURRENT_LIE')}: ${this.getDescriptionText(selection.description)}`);
                        } else if (placement.code === 'LeanAdjustment') {
                            names.push(`${this.$t('CURRENT_LEAN')}: ${this.getDescriptionText(selection.description)}`);
                        } else {
                            names.push(this.getDescriptionText(selection.description));
                        }
                    } else if (hasMain) {
                        names.push(index === 0 ? '─ ─' : '');
                    } else {
                        // eslint-disable-next-line no-lonely-if
                        if (placement.code === 'PutterShaftBand' && !this.customizerStore.groupsSeen.includes('Face/Sole')) {
                            // Add lock message to Shaft Band placement
                            // names.push(`${this.$t('SHAFT_BAND_LOCK_MSG')}`);
                        } else {
                            names.push('─ ─');
                        }
                    }

                    // The first placement may also contain custom text.
                    if (selection?.custom?.['v-stamping-text-1']) {
                        if (selection?.custom?.['v-stamping-text-1'].length > 0) {
                            // Find line 1 color? Grumble to hardcoding.
                            const color = this.placements.find((x) => x.code === 'StampingColorsLine1');
                            if (color) {
                                const colorSelection = this.viewController.selectedAtPlacement(color.code);
                                if (colorSelection && !colorSelection.placeholder) {
                                    names.push(`${selection.custom['v-stamping-text-1']}: ${this.getDescriptionText(colorSelection.description)}`);
                                }
                            }
                        }
                    }

                    if (selection?.custom?.['v-stamping-text-2']) {
                        if (selection.custom['v-stamping-text-2'].length > 0) {
                            // Find line 2 color? Grumble to hardcoding.
                            const color = this.placements.find((x) => x.code === 'StampingColorsLine2');
                            if (color) {
                                const colorSelection = this.viewController.selectedAtPlacement(color.code);
                                if (colorSelection && !colorSelection.placeholder) {
                                    names.push(`${selection.custom['v-stamping-text-2']}: ${this.getDescriptionText(colorSelection.description)}`);
                                }
                            }
                        }
                    }

                    // Laser etching custom text
                    if (selection?.custom?.['v-etching-text-1']) {
                        names.push(selection.custom['v-etching-text-1']);
                    }

                    // If stamping... find stamping color.
                    if (placement.code === 'StampSymbols' && selection && !selection.placeholder) {
                        const symbol = this.placements.find((x) => x.code === 'StampSymbols');
                        const color = this.placements.find((x) => x.code === 'StampingColorsSymbols');

                        if (symbol && color) {
                            const symbolSelection = this.viewController.selectedAtPlacement(symbol.code);
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);

                            if (symbolSelection && colorSelection && !symbolSelection.placeholder && !colorSelection.placeholder) {
                                names.push(`${this.getDescriptionText(symbolSelection.description)}: ${this.getDescriptionText(colorSelection.description)}`);
                            }
                        }
                    }

                    index += 1;
                });

                // label loft, lie, weight here
                const notEmpty = names.filter((x) => x.length > 0);

                return notEmpty;
            },

            isSurefitHubLocked() {
                return this.customizerStore.isSurefitHubLocked(this.placements);
            },
        },

        methods: {
            isAnotherPaintfillComplete(groupName) {
                return ['Face/Sole', 'Top', 'Back']
                    .some((name) => name !== groupName && this.customizerStore.isPaintfillComplete(name));
            },

            getDescriptionText(description) {
                if (description == null) {
                    return '';
                }

                let text = '';

                description.forEach((desc) => {
                    text += ` ${desc.name}`;
                });

                return text.trim();
            },

            getCategorySelections(placements) {
                const names = [];
                const noop = () => {};
                let hasProcessedStamp = false;

                placements.forEach((placement) => {
                    const selectedAtPlacement = this.viewController?.selectedAtPlacement(placement.code);
                    const selection = selectedAtPlacement?.placeholder ? null : selectedAtPlacement;

                    // Skip color placements - these are handled when displaying
                    // text with their colors, but keep StampColor for Stamps to handle separately
                    const colorPlacementCodes = [
                        'StampingColorsLine1',
                        'StampingColorsLine2',
                        'StampingColorsSymbols',
                    ];
                    if (colorPlacementCodes.includes(placement.code)) {
                        return;
                    }

                    if (placement.code === 'StampingStyle' && selectedAtPlacement) {
                        const isPlaceholder = selectedAtPlacement.placeholder;
                        const isOptionalPlaceholder = selectedAtPlacement.placeholderOptional;

                        if (isPlaceholder && !isOptionalPlaceholder) {
                            // names.push('Initials: None');
                        }
                    }

                    if (placement.code === 'StampColor') {
                        return;
                    }

                    if (placement.code === 'Stamp' && selectedAtPlacement && selectedAtPlacement.code !== 'Stamp-Stamp-NoThanks') {
                        if (!hasProcessedStamp) {
                            const isPlaceholder = selectedAtPlacement.placeholder;
                            const isOptionalPlaceholder = selectedAtPlacement.placeholderOptional;

                            if (isPlaceholder && !isOptionalPlaceholder) {
                                // names.push('Stamps: None');
                            } else {
                                names.push('Stamps: Face/Toe');
                            }
                            hasProcessedStamp = true;
                        }
                    }

                    if (selection) {
                        if (placement.code === 'Stamp') {
                            if (selectedAtPlacement.code === 'Stamp-Stamp-NoThanks') {
                                // if (names.length === 0) {
                                //     names.push('No Toe Engraving');
                                // }
                            } else {
                                const stampName = this.getDescriptionText(selectedAtPlacement.description);
                                const stampColor = placements.find((x) => x.code === 'StampColor');
                                if (stampColor) {
                                    const colorSelection = this.viewController.selectedAtPlacement(stampColor.code);
                                    if (colorSelection && !colorSelection.placeholder) {
                                        const colorName = this.getDescriptionText(colorSelection.description);
                                        names.push(`${stampName}: ${colorName}`);
                                    } else {
                                        names.push(stampName);
                                    }
                                } else {
                                    names.push(stampName);
                                }
                            }
                        } else if (placement.code === 'StampingStyle') {
                            if (!selectedAtPlacement.placeholder || selectedAtPlacement.placeholderOptional) {
                                if (selectedAtPlacement.code === 'StampingStyle-StampingStyle-1') {
                                    // if (names.length === 0) {
                                    //     names.push('No Character Stamping');
                                    // }
                                } else {
                                    const custom = selectedAtPlacement.custom || {};
                                    const thumbnailAsset = custom['v-thumbnail-asset'] || '';

                                    let letterStyle = '';
                                    if (thumbnailAsset.toLowerCase().includes('dancing')) {
                                        letterStyle = 'Dancing';
                                    } else if (thumbnailAsset.toLowerCase().includes('straight')) {
                                        letterStyle = 'Straight';
                                    }

                                    const descriptionText = this.getDescriptionText(selectedAtPlacement.description);
                                    const hasDots = descriptionText.toLowerCase().includes('dots');

                                    let stampedName = 'Initials';
                                    if (letterStyle) {
                                        stampedName += `: ${letterStyle}`;
                                    }

                                    if (hasDots) {
                                        stampedName += ' | Separate With Periods';
                                    }

                                    names.push(stampedName);
                                }
                            }
                        } else if (placement.code === 'StampSymbols') {
                            // Symbols are handled later with text
                            noop();
                        } else {
                            names.push(this.getDescriptionText(selectedAtPlacement.description));
                        }
                    }

                    // Handle custom text for Character Stamping
                    if (selection?.custom?.['v-stamping-text-1'] && selection?.custom?.['v-stamping-text-1'].length > 0) {
                        const color = placements.find((x) => x.code === 'StampingColorsLine1');
                        if (color) {
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);
                            if (colorSelection && !colorSelection.placeholder) {
                                names.push(`${selection.custom['v-stamping-text-1']}: ${this.getDescriptionText(colorSelection.description)}`);
                            }
                        }
                    }

                    if (selection?.custom?.['v-stamping-text-2'] && selection.custom['v-stamping-text-2'].length > 0) {
                        const color = placements.find((x) => x.code === 'StampingColorsLine2');
                        if (color) {
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);
                            if (colorSelection && !colorSelection.placeholder) {
                                names.push(`${selection.custom['v-stamping-text-2']}: ${this.getDescriptionText(colorSelection.description)}`);
                            }
                        }
                    }

                    // Fallback: if Character Stamping is selected but text is empty, still show chosen color(s)
                    if (placement.code === 'StampingStyle') {
                        const hasText1 = !!selection?.custom?.['v-stamping-text-1'];
                        const hasText2 = !!selection?.custom?.['v-stamping-text-2'];

                        if (!hasText1) {
                            const color1Placement = placements.find((x) => x.code === 'StampingColorsLine1');
                            if (color1Placement) {
                                const color1Selection = this.viewController.selectedAtPlacement(color1Placement.code);
                                if (color1Selection && !color1Selection.placeholder) {
                                    names.push(`${this.$t('CURRENT_COLOR')}: ${this.getDescriptionText(color1Selection.description)}`);
                                }
                            }
                        }

                        if (!hasText2) {
                            const color2Placement = placements.find((x) => x.code === 'StampingColorsLine2');
                            if (color2Placement) {
                                const color2Selection = this.viewController.selectedAtPlacement(color2Placement.code);
                                if (color2Selection && !color2Selection.placeholder) {
                                    names.push(`${this.$t('CURRENT_COLOR')}: ${this.getDescriptionText(color2Selection.description)}`);
                                }
                            }
                        }
                    }

                    // Handle Symbols and colors
                    if (placement.code === 'StampSymbols' && selection && !selection.placeholder) {
                        const symbol = placements.find((x) => x.code === 'StampSymbols');
                        const color = placements.find((x) => x.code === 'StampingColorsSymbols');
                        if (symbol && color) {
                            const symbolSelection = this.viewController.selectedAtPlacement(symbol.code);
                            const colorSelection = this.viewController.selectedAtPlacement(color.code);

                            if (symbolSelection && colorSelection && !symbolSelection.placeholder && !colorSelection.placeholder) {
                                names.push(`${this.getDescriptionText(symbolSelection.description)}: ${this.getDescriptionText(colorSelection.description)}`);
                            }
                        }
                    }
                });

                const filtered = names.filter((x) => x && x.length > 0);
                return filtered.length > 0 ? filtered.join(' | ') : null;
            },

            getSelectionsForFrontGroup() {
                const results = [];

                // if it hasnt been seen, return ─ ─

                const hasBeenSeen = this.customizerStore.groupsSeen.includes('Face/Sole')
                    || this.customizerStore.groupsSeen.includes('Character Stamping');
                const shouldShowPaintfillRequired = this.isAnotherPaintfillComplete('Face/Sole')
                    && !this.customizerStore.isPaintfillComplete('Face/Sole');
                const noCustomizationSelected = this.customizerStore.isFrontNoCustomizationSelected('Face/Sole');

                if (!hasBeenSeen && !shouldShowPaintfillRequired && !noCustomizationSelected) {
                    return [this.addPlaceholderCopy];
                }

                // Character Stamping (Initials) placements
                const characterStampingPlacements = this.placements.filter((p) => (
                    p.code === 'StampingStyle'
                    || p.code.includes('StampingColorsLine')
                    || p.code === 'StampSymbols'
                    || p.code === 'StampingColorsSymbols'
                ));

                let initials = null;
                if (characterStampingPlacements.length > 0) {
                    initials = this.getCategorySelections(characterStampingPlacements);
                }

                // Stamps placements
                const stampsPlacements = this.placements.filter((p) => (
                    p.code === 'Stamp' || p.code === 'StampColor'
                ));

                let stamps = null;
                if (stampsPlacements.length > 0) {
                    stamps = this.getCategorySelections(stampsPlacements);
                }

                // Check if either selection is "None"
                const initialsIsNone = initials && initials.includes('Initials: None');
                const stampsIsNone = stamps && stamps.includes('Stamps: None');

                // If both are "None", show both. Otherwise, hide "None" entries.
                const bothAreNone = initialsIsNone && stampsIsNone;
                const shouldShowInitials = initials;
                const shouldShowStamps = stamps;

                if (shouldShowInitials && !bothAreNone) {
                    results.push(initials);
                }
                if (shouldShowStamps && !bothAreNone) {
                    results.push(stamps);
                }

                if (noCustomizationSelected) {
                    results.push(this.$t('NO_CUSTOMIZATION'));
                }

                if (shouldShowPaintfillRequired) {
                    results.push(this.$t('PAINTFILL_REQUIRED'));
                }

                return results;
            },
        },
    };
</script>
